import { useCallback, useEffect, useState } from "react";
import {
  fetchQuarantineById,
  updateQuarantineCorrections,
  promoteQuarantine,
  discardQuarantine,
} from "../../../services/aro/quarantineService";
import { QuarantineDetail } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import Toast          from "../../shared/Toast/Toast";
import { formatDateTime, formatLabel } from "../../../utils/formatters";
import { useAuth } from "../../../context/useAuth";
import "./QuarantineDetailPage.css";

interface Props {
  quarantineId: string;
  onBack: () => void;
}

function statusVariant(status: QuarantineDetail["status"]) {
  if (status === "promoted")  return "green";
  if (status === "discarded") return "gray";
  return "amber";
}

export default function QuarantineDetailPage({ quarantineId, onBack }: Props) {
  const { email: changedBy } = useAuth();
  const [row,     setRow]     = useState<QuarantineDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [toast,   setToast]   = useState<{ msg: string; variant: "success" | "info" | "error" } | null>(null);

  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [saving,       setSaving]     = useState(false);
  const [promoting,    setPromoting]  = useState(false);
  const [discarding,   setDiscarding] = useState(false);
  const [actionError,  setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchQuarantineById(quarantineId);
      setRow(detail);
      setCorrections(detail.corrected_fields ?? {});
    } catch (e: any) {
      setError(e.message ?? "Failed to load quarantine row.");
    } finally {
      setLoading(false);
    }
  }, [quarantineId]);

  useEffect(() => { load(); }, [load]);

  const isEditable = row?.status === "quarantined";

  const handleCorrectionChange = (key: string, value: string) => {
    setCorrections((c) => ({ ...c, [key]: value }));
  };

  const handleSaveCorrections = async () => {
    if (!row) return;
    setSaving(true);
    setActionError(null);
    try {
      const updated = await updateQuarantineCorrections(row.id, corrections, changedBy);
      setRow(updated);
      setCorrections(updated.corrected_fields ?? {});
      setToast({ msg: "Corrections saved.", variant: "success" });
    } catch (e: any) {
      setActionError(e.message ?? "Failed to save corrections.");
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    if (!row) return;
    setPromoting(true);
    setActionError(null);
    try {
      const result = await promoteQuarantine(row.id, changedBy);
      if (result.status === "promoted") {
        setToast({ msg: "Row promoted successfully.", variant: "success" });
      } else {
        setToast({ msg: `Still quarantined: ${formatLabel(result.reason_detail ?? result.reason)}`, variant: "error" });
      }
      await load();
    } catch (e: any) {
      setActionError(e.message ?? "Failed to promote row.");
    } finally {
      setPromoting(false);
    }
  };

  const handleDiscard = async () => {
    if (!row) return;
    setDiscarding(true);
    setActionError(null);
    try {
      await discardQuarantine(row.id, changedBy);
      setToast({ msg: "Row discarded.", variant: "info" });
      await load();
    } catch (e: any) {
      setActionError(e.message ?? "Failed to discard row.");
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="aro-page">
      <Toast message={toast?.msg ?? null} variant={toast?.variant} onClose={() => setToast(null)} />

      <button className="aro-back-btn" onClick={onBack}>Back to Quarantine</button>

      {error && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : row ? (
        <>
          <PageHeader
            title={`Quarantine Row #${row.row_number}`}
            subtitle={`File ${row.file_id}`}
            right={<AROBadge label={formatLabel(row.status)} variant={statusVariant(row.status)} />}
          />

          <div className="aro-card">
            <h2 className="aro-section-title">Details</h2>
            <div className="quarantine-detail-grid">
              <div className="quarantine-detail-field">
                <span className="quarantine-detail-field__label">Reason</span>
                <span>{formatLabel(row.reason)}</span>
              </div>
              {row.reason_detail && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Reason Detail</span>
                  <span>{row.reason_detail}</span>
                </div>
              )}
              <div className="quarantine-detail-field">
                <span className="quarantine-detail-field__label">Created</span>
                <span>{formatDateTime(row.created_at)}</span>
              </div>
              {row.resolved_by && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Resolved By</span>
                  <span>{row.resolved_by}</span>
                </div>
              )}
              {row.resolved_at && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Resolved At</span>
                  <span>{formatDateTime(row.resolved_at)}</span>
                </div>
              )}
              {row.promoted_person_id != null && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Promoted Person ID</span>
                  <span>{row.promoted_person_id}</span>
                </div>
              )}
              {row.promoted_certificate_id != null && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Promoted Certificate ID</span>
                  <span>{row.promoted_certificate_id}</span>
                </div>
              )}
            </div>
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Original Row Data</h2>
            <table className="aro-table">
              <tbody>
                {Object.entries(row.raw_row).map(([key, value]) => (
                  <tr key={key}>
                    <td className="aro-table__cell--primary quarantine-raw-row__key">{key}</td>
                    <td>{value || <span className="aro-muted">(empty)</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Corrections</h2>
            {!isEditable && (
              <p className="aro-muted">
                This row is {row.status} — corrections can only be made while a row is quarantined.
              </p>
            )}
            <div className="quarantine-corrections-grid">
              {Object.keys(row.raw_row).map((key) => (
                <div key={key} className="quarantine-edit-row">
                  <label className="quarantine-edit-label">{key}</label>
                  <input
                    className="aro-input"
                    disabled={!isEditable}
                    value={corrections[key] ?? ""}
                    placeholder={row.raw_row[key] || "Enter corrected value…"}
                    onChange={(e) => handleCorrectionChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {actionError && <div className="aro-error-banner">{actionError}</div>}

            <div className="quarantine-detail-actions">
              {isEditable && (
                <button className="aro-btn aro-btn--ghost" onClick={handleSaveCorrections} disabled={saving}>
                  {saving ? "Saving…" : "Save Corrections"}
                </button>
              )}
              {isEditable && (
                <button className="aro-btn aro-btn--ghost" onClick={handleDiscard} disabled={discarding}>
                  {discarding ? "Discarding…" : "Discard"}
                </button>
              )}
              {isEditable && (
                <button className="aro-btn aro-btn--primary" onClick={handlePromote} disabled={promoting}>
                  {promoting ? "Promoting…" : "Promote"}
                </button>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
